/**
 * Cookie consent state for the dashboard.
 *
 * Mirrors the design used on the marketing site and chat UI:
 * - EU/UK/EEA visitors (timezone-based) get an opt-in flow: analytics stays
 *   OFF until "Accept all".
 * - Non-EU visitors get an opt-out flow: analytics runs immediately, banner
 *   stays visible until the user accepts or rejects.
 * - Decision is persisted in localStorage under CONSENT_STORAGE_KEY.
 *
 * Keep this file in sync with:
 *   marketing_websites/qualzai_marketing/src/lib/consent.ts
 *   participant/chat_ui/src/utils/cookieConsent.ts
 */
export const CONSENT_STORAGE_KEY = 'qualz_cookie_consent_v1';
export const CONSENT_VERSION = 1;
export const CONSENT_CHANGE_EVENT = 'qualz:consent-change';

const EXTRA_EU_TIMEZONES = new Set([
  'Atlantic/Reykjavik',
  'Atlantic/Faroe',
  'Atlantic/Azores',
  'Atlantic/Canary',
  'Atlantic/Madeira',
]);

export function isEURegion() {
  if (typeof Intl === 'undefined') return true;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return tz.startsWith('Europe/') || EXTRA_EU_TIMEZONES.has(tz);
  } catch {
    return true;
  }
}

export function getConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    if (parsed.status !== 'accepted' && parsed.status !== 'rejected') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(status) {
  const record = {
    version: CONSENT_VERSION,
    status,
    timestamp: new Date().toISOString(),
    region: isEURegion() ? 'eu' : 'non-eu',
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
      window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: record }));
    } catch {
      // ignore — private mode or quota
    }
  }
  return record;
}

export function shouldRunAnalytics() {
  const stored = getConsent();
  if (stored) return stored.status === 'accepted';
  return !isEURegion();
}

export function hasMadeDecision() {
  return getConsent() !== null;
}

export function onConsentChange(listener) {
  if (typeof window === 'undefined') return () => {};
  const handler = (e) => {
    if (e.detail) listener(e.detail);
  };
  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}
